import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import TableHeadFiles from "./TableHeadFiles";
import download from "../assests/images/download.svg";
import deleteIcon from "../assests/images/delete.svg";
import FilterMenuUpload from "./utilities/FilterMenuUpload";
import useResponsiveFlags from "../hooks/useResponsiveFlags";
import { Modal } from "react-bootstrap";
import axios from "../api/axios";

export default function ManageUpload(props) {
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [data, setData] = useState(props.data);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");
  const { isSmallMobileScreen } = useResponsiveFlags();
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState(""); // For global search
  const [filters, setFilters] = useState({
    fileType: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        if (successMessage) {
          setSuccessMessage("");
        } else if (errorMessage) {
          setErrorMessage("");
        }
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
  };

  const filteredData = useMemo(() => {
    if (data)
    return data.filter((row) => {
      const searchableFields = ["title", "name"];

      const matchesSearch = searchableFields.some((field) =>
        row[field]?.toString().toLowerCase().includes(search)
      );
      const parsedRowDate = row.date ? new Date(row.date) : null;

      const filterStartDate = filters.startDate
        ? new Date(filters.startDate)
        : null;
      const filterEndDate = filters.endDate ? new Date(filters.endDate) : null;
      const matchesFilters =
        (!filters.fileType ||
          row.ftype?.toLowerCase() === filters.fileType.toLowerCase()) &&
        (!filterStartDate || parsedRowDate >= filterStartDate) &&
        (!filterEndDate || parsedRowDate <= filterEndDate);

      return matchesSearch && matchesFilters;
    });
  }, [data, filters, search]);

  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...filteredData].sort(getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (orderBy === "date") {
      valueA = valueA ? new Date(valueA) : null;
      valueB = valueB ? new Date(valueB) : null;
    }

    if(orderBy === "size"){
      valueA = Number(valueA);
      valueB = Number(valueB);
    }

    if (typeof valueA === "string" && orderBy !== "size") valueA = valueA.toLowerCase();
    if (typeof valueB === "string" && orderBy !== "size") valueB = valueB.toLowerCase();
    
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes >= 1073741824) {
      // 1 GB = 1024^3 bytes
      return `${(sizeInBytes / 1073741824).toFixed(2)} GB`;
    } else if (sizeInBytes >= 1048576) {
      // 1 MB = 1024^2 bytes
      return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
    } else if (sizeInBytes >= 1024) {
      // 1 KB = 1024 bytes
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else {
      return `${sizeInBytes} B`;
    }
  };

  const handleDownload = async (fileId) => {
    axios
      .post("/api/download-user-file", {
        fileid: fileId,
      })
      .then((res) => {
        const downloadUrl = res.data.downloadUrl;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((err) => {
        setErrorMessage(
          err.response?.data?.message
            ? err.response.data.message
            : err.response?.data?.error
            ? err.response.data.error
            : `Failed to download the requested file`
        );
      });
  };
  const deleteSingleFile = async (file) => {
    try {
      const response = await axios.post("/api/delete-user-file", {
        fileid: file.id,
      });
      if (response.data?.success) {
        return { success: true, file };
      } else {
        return {
          success: false,
          file,
          message: response.data?.message || "Delete failed",
        };
      }
    } catch (err) {
      return {
        success: false,
        file,
        message:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Delete failed",
      };
    }
  };
  const confirmDelete = async () => {
    if (!deleteTarget || deleteTarget.length === 0) return;

    const successful = [];
    const failed = [];

    for (const file of deleteTarget) {
      const result = await deleteSingleFile(file);
      if (result.success) {
        successful.push(file);
      } else {
        failed.push({
          ...file,
          message: result.message
        });
      }
    }

    if (successful.length > 0) {
      setData((prev) =>
        prev.filter((file) => !successful.some((del) => del.id === file.id))
      );

      setSelectedFiles((prev) =>
        prev.filter((id) => !successful.some((del) => del.id === id))
      );
    }

    if (successful.length > 0) {
      setSuccessMessage(`${successful.length} file(s) deleted.`);
    }

    if (failed.length > 0) {
      if (failed.length > 0) {
        setErrorMessage(
          failed
            .map((f) => `${f.name}: ${f.message}`)
            .join(", ")
        );
      }
    }
    props.refreshData();
    setDeleteTarget(null);
    setSelectedFiles([]);
  };
  
  const handleSelectAll = (isChecked) => {
    setSelectedFiles(isChecked ? sortedData.map(({ id }) => id) : []);
  };

  const handleSelectFile = (fileId, isChecked) => {
    setSelectedFiles((prev) => {
      if (isChecked) {
        return prev.includes(fileId) ? prev : [...prev, fileId];
      } else {
        return prev.filter((id) => id !== fileId);
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${dayName} ${month}/${day}/${year}`;
  };

  return (
    <>
      <div className="d-flex flex-column-reverse flex-xl-row justify-content-between mb-xl-3">
        <div className="d-flex align-items-center">
          <input
            type="text"
            className="form-control search-icon"
            placeholder="Search"
            // value={searchWord}
            onChange={handleSearchChange}
          />
          <FilterMenuUpload
            handleFilterChange={handleFilterChange}
            filters={filters}
          />
          {selectedFiles?.length > 0 && (
            <button
              className="btn btn-danger text-white ms-3"
              onClick={() => {
                const selectedFileObjects = data.filter((file) =>
                  selectedFiles?.includes(file.id)
                );
                setDeleteTarget(selectedFileObjects);
              }}
              // onClick={handleDeleteSelected}
              // style={{ background: "#dc3545" }}
            >
              Delete Selected Files
            </button>
          )}
        </div>
        <div className="storage-bar mb-3 mb-xl-0">
          <p className="d-flex justify-content-between">
            <span className="storage-limit">Storage Limit</span>{" "}
            <span>{props.size} MB / 1000 MB</span>
          </p>
          <div className="progress-bar">
            <div
              className="progress-files"
              style={{ width: `${props.size / 10}%` }}
            ></div>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div id="error-message" className="error-message">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div id="success-message" className="success-message">
          {successMessage}
        </div>
      )}
      <TableContainer>
        <Table stickyHeader>
          <TableHeadFiles
            order={order}
            orderBy={orderBy}
            onSort={handleSort}
            onSelect={handleSelectAll}
            selectedRow={selectedFiles}
            isAllSelected={
              sortedData?.length > 0 &&
              selectedFiles?.length > 0 &&
              selectedFiles.length === sortedData.length
            }
          />
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {data.length === 0
                    ? "No files found"
                    : "No matching records found."}
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell
                    style={
                      !isSmallMobileScreen
                        ? {
                            position: "sticky",
                            top: 0,
                            left: 0,
                            zIndex: 2,
                            background: "#fff",
                          }
                        : {}
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedFiles?.includes(row.id)}
                      onChange={(e) =>
                        handleSelectFile(row.id, e.target.checked)
                      }
                    />
                  </TableCell>
                  <TableCell
                    style={
                      !isSmallMobileScreen
                        ? {
                            position: "sticky",
                            top: 0,
                            left: 45,
                            zIndex: 1,
                            background: "#fff",
                          }
                        : {}
                    }
                    className="custom-size"
                  >
                    {row.title}
                  </TableCell>
                  <TableCell className="custom-size">{row.name}</TableCell>
                  <TableCell className="custom-size">{row.ftype}</TableCell>
                  <TableCell className="custom-size">
                    {formatDate(row.date)}
                  </TableCell>
                  <TableCell className="custom-size">
                    {formatFileSize(row.size)}
                  </TableCell>
                  <TableCell>
                    <BootstrapTooltip title="Download File">
                      <button
                        className="btn btn-sm"
                        onClick={() => handleDownload(row.id)}
                      >
                        <img src={download} alt="Download" />
                      </button>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Delete">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setDeleteTarget([row])}
                        // onClick={() => handleDelete(row.id)}
                      >
                        <img src={deleteIcon} alt="Delete" />
                      </button>
                    </BootstrapTooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {deleteTarget && (
        <>
          <Modal
            show={!!deleteTarget}
            onHide={() => setDeleteTarget(null)}
            centered
            backdrop="static" // Prevent closing on overlay click
            keyboard={false}
            contentClassName="p-4"
          >
            <Modal.Header className="flex-column p-0 border-0">
              <p className="sub-step-bold mb-0">Confirm Deletion</p>
            </Modal.Header>
            <Modal.Body className="mt-4 mb-4 p-0">
              {deleteTarget.length === 1 ? (
                <p className="text-center m-0">
                  Are you sure you want to delete{" "}
                  <strong>{deleteTarget[0].name}</strong>? Once deleted, you
                  cannot retrieve the file.
                </p>
              ) : (
                <>
                  <p className="text-center m-0">
                    Are you sure you want to delete the below files? Once
                    deleted you will not be able to retrieve them.
                  </p>
                  <ul>
                    {deleteTarget.map((file) => (
                      <li key={file.id}>{file.title}</li>
                    ))}
                  </ul>
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="justify-content-center border-0 p-0">
              <div className="m-0 me-2">
                <button
                  onClick={confirmDelete}
                  className="learnmore-btn m-0"
                  style={{ padding: "12px 24px" }}
                >
                  Delete
                </button>
              </div>
              <div className="m-0 ms-2">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="cancel-btn m-0"
                  style={{ padding: "12px 24px" }}
                >
                  Cancel
                </button>
              </div>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
}
